import type { Cart } from "@graphql/generated/types";
import { DiscountCodeState } from "@graphql/generated/types";
import { getPromoCodeDiscountValue } from "./getPromoCodeDiscountValue";
import { removeCountryPrefix } from "./removeCountryPrefix";

const shippingDiscountCentsForCode = (cart: Cart, discountCodeId: string): number | null => {
    const targetIds =
        cart.discountCodes
            ?.find((c) => c.discountCode.id === discountCodeId)
            ?.discountCode?.cartDiscounts?.map((d) => d.id)
            .filter(Boolean) ?? [];
    if (targetIds.length === 0) {
        return null;
    }
    let sum = 0;
    for (const inc of cart.shippingInfo?.discountedPrice?.includedDiscounts ?? []) {
        if (inc.discount?.id && targetIds.includes(inc.discount.id)) {
            sum += inc.discountedAmount?.centAmount ?? 0;
        }
    }
    return sum;
};

export type PromoCodeOnCartBreakdown = {
    discountCodeId: string;
    code: string;
    state: DiscountCodeState;
    lineItemsDiscountCentAmount: number | null;
    shippingDiscountCentAmount: number | null;
    totalAttributedDiscountCentAmount: number | null;
    currency: string | null;
};

/** MatchesCart with no line discount cents and mapped shipping discount of 0 → remove + no-effect message. */
export const isPromoCodeMatchesCartWithNoDetectableEffect = (
    row: PromoCodeOnCartBreakdown
): boolean =>
    row.state === DiscountCodeState.MatchesCart &&
    row.lineItemsDiscountCentAmount === null &&
    row.shippingDiscountCentAmount === 0;

/** Line + shipping attribution per discount-code row (debug / diagnostics). */
export const buildPromoCodesDiscountBreakdown = (
    cart: Cart,
    countryISOCode: string
): PromoCodeOnCartBreakdown[] => {
    const rows: PromoCodeOnCartBreakdown[] = [];

    for (const entry of cart.discountCodes ?? []) {
        const dc = entry?.discountCode;
        const id = dc?.id;
        if (!id || entry?.state == null) {
            continue;
        }

        const lineMoney = getPromoCodeDiscountValue(id, cart);
        const lineCents = lineMoney?.centAmount ?? null;
        const shipCents = shippingDiscountCentsForCode(cart, id);
        const totalAttributedCents =
            lineCents != null || shipCents != null ? (lineCents ?? 0) + (shipCents ?? 0) : null;

        rows.push({
            discountCodeId: id,
            code: removeCountryPrefix(dc.code ?? "", countryISOCode),
            state: entry.state,
            lineItemsDiscountCentAmount: lineCents,
            shippingDiscountCentAmount: shipCents,
            totalAttributedDiscountCentAmount: totalAttributedCents,
            currency: lineMoney?.currencyCode ?? cart.totalPrice?.currencyCode ?? null,
        });
    }

    return rows;
};
